import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import {asyncScheduler, of, Subject} from 'rxjs';
import { map, tap, filter, observeOn, switchMap, take, delay, takeUntil } from 'rxjs/operators';
import { ROUTER_NAVIGATED, ROUTER_REQUEST, RouterNavigatedAction, RouterRequestAction } from '@ngrx/router-store';
import * as Sentry from '@sentry/browser';

import { IdentityService } from '~core/modules/identity/services/identity.service';
import { UrlUtil } from '~shared/utils/url.util';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '~env/environment';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { LoggerService } from '~shared/modules/logger/services/logger.service';
import { LoggerStrategies } from '~shared/modules/logger/models';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import {
  FF_ACCOUNT_INFO_V2,
  FF_AUTH_CHECK_DELAY,
  FF_LOGIN_MONITORING_LOGGING_STRATEGY,
  FF_SL_FORCE_REDIRECTION_TO_VALIDATE_EMAIL,
} from '~app/modules/authorization/constants/authorization.constant';
import { AuthService } from '~app/modules/authorization/services/authorization-idam.service';
import {
  NUMBER_10000,
  APP_EMPLOYEE_CUSTOMERS_LIST,
  APP_USER_VALIDATE_EMAIL_ROUTE,
  APP_USER_IDAM_REGISTER_ROUTE,
} from '~shared/constants';
import { MagicLinkEffectsService } from '~app/modules/authorization/services/magic-link-effects.service';
import { PasswordResetService } from '~app/modules/authorization/services/password-reset.service';
import { AccountType } from '~shared/models';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';
import { deleteSoftLoginSession } from '~server/utils/auth.utils';

@Injectable()
export class RouterEffects implements OnDestroy {
  authCheckDelay = NUMBER_10000;
  private unsubscribe$ = new Subject<void>();
  isAccountInfoV2APIEnabled$ =
    this.activeFeatureToggleService.isFeatureEnabledReadyForCurrentCountry$(FF_ACCOUNT_INFO_V2);
  isForceRedirectionEnabled$ = this.activeFeatureToggleService.isFeatureEnabledReadyForCurrentCountry$(
    FF_SL_FORCE_REDIRECTION_TO_VALIDATE_EMAIL
  );

  constructor(
    private readonly actions$: Actions,
    private readonly identityService: IdentityService,
    private authService: AuthService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly serverLoggerService: ServerLoggerService,
    private activeFeatureToggleService: ActiveFeaturesConfigcatService,
    private readonly loggerService: LoggerService,
    private readonly magicLinkEffectsService: MagicLinkEffectsService,
    private readonly passwordResetService: PasswordResetService
  ) {
    this.activeFeatureToggleService
      .getFeatureValue(FF_AUTH_CHECK_DELAY, NUMBER_10000.toString())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.authCheckDelay = Number(res);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  @Effect({ dispatch: false })
  trackNavigationStart$ = this.actions$.pipe(
    ofType(ROUTER_REQUEST),
    switchMap((e: RouterRequestAction) => {
      if (!isPlatformBrowser(this.platformId) || !environment.sentryDsn()) {
        // Skip errors when we don't have proper env dns url for Sentry
        return of(null);
      }

      Sentry.captureEvent({
        message: `Angular Route Navigation started from ${e.payload.event.url}. Url: ${window.location.href}`,
      });

      return of(null);
    })
  );

  @Effect({ dispatch: false })
  trackNavigationCompletion$ = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    switchMap((e: RouterNavigatedAction) => {
      if (!isPlatformBrowser(this.platformId) || !environment.sentryDsn()) {
        // Skip errors when we don't have proper env dns url for Sentry
        return of(null);
      }

      Sentry.captureEvent({
        message: `Angular Route Navigation finished to ${e.payload.routerState.url}. Url: ${window.location.href}`,
      });

      return of(null);
    })
  );

  @Effect({ dispatch: false })
  removeAuthCodeFlowUrlParams$ = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    observeOn(asyncScheduler),
    switchMap(() =>
      this.identityService.isAuthorized$.pipe(
        filter(Boolean),
        take(1),
        tap(() => {
          const query = new URL(window.location.href).searchParams;
          const discountCodeEncoded = query.get('discount_code');
          // Get discount code via shared payment link [DEPRECATED]
          if (discountCodeEncoded) {
            try {
              CheckoutCommonUtil.saveTmpDiscountCodeAppliedToSessionStorage(atob(discountCodeEncoded));
            } catch {}
          } else {
            // Get discount code via shared payment link (localstorage implementation)
            const discountCode = localStorage.getItem('app-generic/discountCode');
            if (discountCode) {
              localStorage.removeItem('app-generic/discountCode');
              CheckoutCommonUtil.saveTmpDiscountCodeAppliedToSessionStorage(discountCode);
            }
          }
          UrlUtil.removeQueryParameters(['discount_code', 'code', 'scope', 'state', 'session_state'], this.router);
        })
      )
    )
  );

  @Effect({ dispatch: false })
  blockNonImpersonatedEmployeeNavigation$ = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    observeOn(asyncScheduler),
    switchMap(() =>
      this.isAccountInfoV2APIEnabled$.pipe(
        tap((enabled) => {
          if (enabled) {
            this.identityService
              .getAuthAccountInfoStatus()
              .pipe(filter((accountStatus) => accountStatus === AccountType.EMPLOYEE))
              .subscribe(() => {
                this.router.navigate([`${APP_EMPLOYEE_CUSTOMERS_LIST}`]);
              });
          }
        })
      )
    )
  );

  @Effect({ dispatch: false })
  checkAuthStatAfterIDAMRedirect$ = this.actions$.pipe(
    ofType(ROUTER_REQUEST),
    map(() => {
      const query = new URL(window.location.href).searchParams;
      const isSuccess = query.has('code') && query.has('state') && query.has('session_state');
      const isFailure = query.has('error');

      const loginState = { success: null, failure: null };

      if (query.has('code') && query.has('session_state')) {
        deleteSoftLoginSession();
      }

      if (isSuccess) {
        loginState.success = {
          code: query.get('code'),
          state: query.get('state'),
          session_state: query.get('session_state'),
        };
      } else if (isFailure) {
        loginState.failure = {
          error: query.get('error'),
          error_description: query.get('error_description'),
          state: query.get('state'),
        };
      }
      return loginState;
    }),
    filter((loginState) => loginState.success || loginState.failure),
    delay(this.authCheckDelay),
    switchMap((loginState) =>
      this.identityService.isAuthorized$.pipe(
        take(1),
        map((authState) => ({ authState, loginState }))
      )
    ),
    switchMap(({ authState, loginState }) =>
      this.activeFeatureToggleService
        .getFeatureValue(FF_LOGIN_MONITORING_LOGGING_STRATEGY, LoggerStrategies.instanaLogger)
        .pipe(map((strategy) => ({ strategy, authState, loginState })))
    ),
    tap(({ strategy, authState, loginState }) => {
      if (!authState && loginState.success) {
        this.loggerService.warning('User was not authorized after IDAM login redirect', loginState.success, strategy);
      } else if (!authState && loginState.failure) {
        this.loggerService.warning('IDAM error during login redirect', loginState.failure, strategy);
      }
    }),
    takeUntil(this.authService.logoutListener())
  );

  @Effect({ dispatch: false })
  removeAutologinSuccessParam$ = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    observeOn(asyncScheduler),
    tap(() => {
      const { queryParams } = this.router.parseUrl(environment.originalUrl());
      const isAutoLoginSuccess = Object.keys(queryParams).includes('autoLoginRedirect');

      if (isAutoLoginSuccess && isPlatformBrowser(this.platformId)) {
        this.serverLoggerService.info('TIER1 SIGNUP: Auto-login after Tier1 signup fallback redirect succeeded', {
          correlationId: queryParams.autoLoginRedirect,
        });
        UrlUtil.removeQueryParameters(['autoLoginRedirect'], this.router);
      }
    })
  );

  @Effect({ dispatch: false })
  magicLinkExpiry$ = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    observeOn(asyncScheduler),
    this.magicLinkEffectsService.magicLinkExpiryObserver()
  );

  @Effect({ dispatch: false })
  resetPasswordSuccess$ = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    observeOn(asyncScheduler),
    this.passwordResetService.resetPasswordSuccessObserver()
  );

  @Effect({ dispatch: false })
  forceRedirectToValidateEmail$ = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    observeOn(asyncScheduler),
    switchMap(() => {
      const isAuthRegisterVisit = this.router.url === `/${APP_USER_IDAM_REGISTER_ROUTE}`;
      return this.isForceRedirectionEnabled$.pipe(
        filter(Boolean),
        tap(() => {
          if (isAuthRegisterVisit) {
            this.router.navigate([APP_USER_VALIDATE_EMAIL_ROUTE]);
          }
        })
      );
    })
  );
}
